import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { Alert, Box, Button, Center, Group, Loader, NumberInput, PasswordInput, Select, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { BillingIntegrationUpdate, BillingProvider } from '@bmai/shared';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { OperatorSettingsViewModel } from './OperatorSettingsViewModel';

function Section({ title, description, children }: { title: string; description: string; children: ReactNode }): ReactElement {
    return (
        <Box>
            <Text size="sm" fw={600}>{title}</Text>
            <Text size="xs" c="dimmed" mb="sm">{description}</Text>
            {children}
        </Box>
    );
}

/** Help text under a secret field: what's on file, and how to keep vs. replace it. */
function secretHint(preview: string | null | undefined): string {
    return preview
        ? `On file: ${preview}. Leave blank to keep it, or enter a new value to replace.`
        : 'Not set. Enter a value to store it.';
}

export default function OperatorBillingIntegrationPage(): ReactElement {
    const vm = useViewModel(OperatorSettingsViewModel);
    const [provider, setProvider] = useState<BillingProvider>('none');
    const [currency, setCurrency] = useState('USD');
    const [netTermsDays, setNetTermsDays] = useState<number | ''>(30);
    const [publishableKey, setPublishableKey] = useState('');
    const [secretKey, setSecretKey] = useState('');
    const [webhookSecret, setWebhookSecret] = useState('');

    useEffect(() => { void vm.loadBilling(); }, [vm]);
    useEffect(() => {
        const b = vm.billing;
        if (!b) return;
        setProvider(b.provider);
        setCurrency(b.currency);
        setNetTermsDays(b.netTermsDays);
        setPublishableKey(b.publishableKey ?? '');
        // Secret fields stay blank — the raw values never leave the vault; only
        // their masked previews are shown as hints.
        setSecretKey('');
        setWebhookSecret('');
    }, [vm.billing]);

    const save = async (): Promise<void> => {
        const update: BillingIntegrationUpdate = {
            provider,
            currency: currency.trim().toUpperCase(),
            netTermsDays: netTermsDays === '' ? 30 : Number(netTermsDays),
            publishableKey: publishableKey.trim(),
            // Only send a secret when the operator typed one — blank leaves the
            // stored value untouched.
            ...(secretKey.trim() ? { secretKey: secretKey.trim() } : {}),
            ...(webhookSecret.trim() ? { webhookSecret: webhookSecret.trim() } : {}),
        };
        try {
            await vm.saveBilling(update);
            setSecretKey('');
            setWebhookSecret('');
            notifications.show({ color: 'green', message: 'Billing integration saved' });
        } catch (e) {
            notifications.show({ color: 'red', title: 'Save failed', message: e instanceof Error ? e.message : 'Unexpected error' });
        }
    };

    const isStripe = provider === 'stripe';

    return (
        <Box>
            <PageHeaderComponent title="Billing integration" subtitle="Payment-provider configuration. Secret keys are encrypted at rest in the platform vault — only a masked preview is ever shown here. Charging (invoices + the scheduled run) is not yet enabled." />

            {vm.error && <Alert color="red" title="Failed to load" mb="md">{vm.error}</Alert>}

            {vm.loading && !vm.billing ? (
                <Center py="xl"><Loader size="sm" /></Center>
            ) : (
                <Stack gap="xl">
                    <Section title="Provider" description="Settlement currency and invoice terms for the payment provider.">
                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg" verticalSpacing="md">
                            <Select
                                label="Provider"
                                data={[{ value: 'none', label: 'None' }, { value: 'stripe', label: 'Stripe' }]}
                                value={provider}
                                onChange={(v) => setProvider((v as BillingProvider) ?? 'none')}
                            />
                            <TextInput label="Settlement currency" placeholder="USD" value={currency} onChange={(e) => setCurrency(e.currentTarget.value)} maxLength={3} />
                            <NumberInput label="Invoice net terms (days)" value={netTermsDays} onChange={(v) => setNetTermsDays(typeof v === 'number' ? v : '')} min={0} max={365} allowDecimal={false} />
                        </SimpleGrid>
                    </Section>

                    {isStripe && (
                        <Section title="Stripe keys" description="The publishable key is public; the secret and webhook signing keys are stored encrypted and never displayed again.">
                            <Stack gap="md" maw={520}>
                                <TextInput label="Publishable key" description="Non-secret Stripe publishable key." placeholder="pk_live_…" value={publishableKey} onChange={(e) => setPublishableKey(e.currentTarget.value)} />
                                <PasswordInput
                                    label="Secret key"
                                    description={secretHint(vm.billing?.secretKeyPreview)}
                                    placeholder={vm.billing?.secretKeyPreview ? '••••••••••••' : 'sk_live_…'}
                                    value={secretKey}
                                    onChange={(e) => setSecretKey(e.currentTarget.value)}
                                />
                                <PasswordInput
                                    label="Webhook signing secret"
                                    description={secretHint(vm.billing?.webhookSecretPreview)}
                                    placeholder={vm.billing?.webhookSecretPreview ? '••••••••••••' : 'whsec_…'}
                                    value={webhookSecret}
                                    onChange={(e) => setWebhookSecret(e.currentTarget.value)}
                                />
                            </Stack>
                        </Section>
                    )}

                    <Group>
                        <Button loading={vm.saving} onClick={() => void save()}>Save integration</Button>
                    </Group>
                </Stack>
            )}
        </Box>
    );
}
